/**
 * Flasher state machine.
 *
 * Holds the single FlashService instance and exposes everything the UI needs as
 * plain state. Every operation that can fail routes its error through
 * diagnose(), so the UI only ever renders a structured Diagnosis and never a
 * raw exception string.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BAUD_RATES,
  DEFAULT_SETTINGS,
  FlashService,
  type DeviceInfo,
  type FlashProgress,
  type FlashSettings,
  type LogLevel,
} from "../flasher/flashService";
import { analyzeBinary, recomputeOffsets, validateLayout, type FirmwarePart } from "../flasher/layout";
import { diagnose, type Diagnosis, type FlashPhase } from "../flasher/errors";
import { checkEnvironment } from "../flasher/environment";
import { filesFromDataTransfer, readFileBytes } from "../flasher/fileIntake";
import { CHIPS, type ChipKey, type ChipProfile } from "../flasher/chips";

export type ConnectionState = "disconnected" | "connecting" | "connected";
export type FlashState = "idle" | "flashing" | "success" | "failed";

export interface LogEntry {
  id: number;
  timestamp: number;
  level: LogLevel;
  message: string;
}

const MAX_LOG_ENTRIES = 1500;
const MAX_MONITOR_CHARS = 200_000;
const SETTINGS_STORAGE_KEY = "esp-web-flasher.settings.v1";

function loadStoredSettings(): FlashSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<FlashSettings>;
    // Merge rather than replace: a stored blob from an older version must not
    // leave newly-added settings undefined.
    const merged = { ...DEFAULT_SETTINGS, ...parsed };
    if (!BAUD_RATES.includes(merged.baudRate as (typeof BAUD_RATES)[number])) {
      merged.baudRate = DEFAULT_SETTINGS.baudRate;
    }
    return merged;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function useFlasher() {
  const environment = useMemo(() => checkEnvironment(), []);

  const [connection, setConnection] = useState<ConnectionState>("disconnected");
  const [device, setDevice] = useState<DeviceInfo | null>(null);
  const [parts, setParts] = useState<FirmwarePart[]>([]);
  const [settings, setSettings] = useState<FlashSettings>(loadStoredSettings);
  const [flashState, setFlashState] = useState<FlashState>("idle");
  const [progress, setProgress] = useState<FlashProgress | null>(null);
  const [diagnosis, setDiagnosis] = useState<Diagnosis | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [monitorText, setMonitorText] = useState("");
  const [monitorActive, setMonitorActive] = useState(false);
  /**
   * A port permission survives disconnecting the loader, which is what lets the
   * serial monitor reopen the same device without a second browser prompt.
   */
  const [hasPort, setHasPort] = useState(false);
  const [openMonitorAfterFlash, setOpenMonitorAfterFlash] = useState(true);
  const [isReadingFiles, setIsReadingFiles] = useState(false);
  /** Target used for offset inference before a board is connected. */
  const [assumedChipKey, setAssumedChipKey] = useState<ChipKey>("esp32");

  // Tracks what the service is doing so a thrown error can be diagnosed with
  // the right phase context.
  const phaseRef = useRef<FlashPhase>("connect");
  const logIdRef = useRef(0);

  const appendLog = useCallback((level: LogLevel, message: string) => {
    setLogs((current) => {
      const next = [...current, { id: logIdRef.current++, timestamp: Date.now(), level, message }];
      return next.length > MAX_LOG_ENTRIES ? next.slice(next.length - MAX_LOG_ENTRIES) : next;
    });
  }, []);

  const serviceRef = useRef<FlashService | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = new FlashService({
      onLog: appendLog,
      onProgress: setProgress,
      onPhase: (phase) => {
        phaseRef.current = phase;
      },
      onDeviceLost: () => {
        setConnection("disconnected");
        setDevice(null);
        setMonitorActive(false);
        setDiagnosis(
          diagnose(new Error("The device has been lost."), { phase: phaseRef.current }),
        );
        setFlashState((state) => (state === "flashing" ? "failed" : state));
      },
      onMonitorData: (text) => {
        setMonitorText((current) => {
          const next = current + text;
          return next.length > MAX_MONITOR_CHARS ? next.slice(next.length - MAX_MONITOR_CHARS) : next;
        });
      },
    });
  }
  const service = serviceRef.current;

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Private browsing modes reject writes; settings just will not persist.
    }
  }, [settings]);

  /**
   * The chip used for offset inference: the connected board when we have one,
   * otherwise whatever the user selected.
   */
  const targetChip: ChipProfile | undefined = device?.chip ?? CHIPS[assumedChipKey];

  const report = useMemo(
    () =>
      validateLayout({
        parts,
        chip: targetChip,
        eraseAll: settings.eraseAll,
        flashSizeBytes: device?.flashSizeBytes ?? null,
      }),
    [parts, targetChip, settings.eraseAll, device?.flashSizeBytes],
  );

  // Offsets are chip-dependent, so re-derive them whenever the target changes.
  const targetChipKey = targetChip?.key;
  useEffect(() => {
    setParts((current) => (current.length === 0 ? current : recomputeOffsets(current, targetChip)));
    // targetChip is derived from targetChipKey; depending on the key avoids
    // re-running on every render from a fresh object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetChipKey]);

  const addFiles = useCallback(
    async (incoming: File[]) => {
      if (incoming.length === 0) return;
      setIsReadingFiles(true);
      try {
        const built: FirmwarePart[] = [];
        for (const file of incoming) {
          try {
            const data = await readFileBytes(file);
            const analysis = analyzeBinary(file.name, data, targetChip);
            built.push({
              id: `${file.name}-${file.size}-${crypto.randomUUID()}`,
              fileName: file.name,
              data,
              offset: analysis.suggestedOffset ?? 0,
              offsetSource: "auto",
              include: true,
              analysis,
            });
            appendLog(
              "info",
              `Loaded ${file.name} (${data.length} bytes), identified as ${analysis.role}.`,
            );
          } catch (error) {
            appendLog(
              "error",
              `Could not read ${file.name}: ${error instanceof Error ? error.message : String(error)}`,
            );
          }
        }

        setParts((current) => {
          // Re-adding a file replaces the previous copy rather than duplicating it.
          const names = new Set(built.map((part) => part.fileName));
          const kept = current.filter((part) => !names.has(part.fileName));
          return recomputeOffsets([...kept, ...built], targetChip);
        });
      } finally {
        setIsReadingFiles(false);
      }
    },
    [appendLog, targetChip],
  );

  const addFromDataTransfer = useCallback(
    async (transfer: DataTransfer) => {
      const files = await filesFromDataTransfer(transfer);
      if (files.length === 0) {
        appendLog("warn", "Nothing usable was dropped. Drop .bin files or the folder containing them.");
        return;
      }
      await addFiles(files);
    },
    [addFiles, appendLog],
  );

  const removePart = useCallback((id: string) => {
    setParts((current) => current.filter((part) => part.id !== id));
  }, []);

  const clearParts = useCallback(() => setParts([]), []);

  const setPartOffset = useCallback((id: string, offset: number) => {
    setParts((current) =>
      current.map((part) => (part.id === id ? { ...part, offset, offsetSource: "manual" } : part)),
    );
  }, []);

  const resetPartOffset = useCallback((id: string) => {
    setParts((current) =>
      current.map((part) =>
        part.id === id && part.analysis.suggestedOffset !== null
          ? { ...part, offset: part.analysis.suggestedOffset, offsetSource: "auto" }
          : part,
      ),
    );
  }, []);

  const togglePart = useCallback((id: string) => {
    setParts((current) =>
      current.map((part) => (part.id === id ? { ...part, include: !part.include } : part)),
    );
  }, []);

  const connect = useCallback(async () => {
    setDiagnosis(null);
    phaseRef.current = "select-port";

    // The monitor holds the same stream the loader needs, so it must be closed
    // before a connection attempt or the port stays locked.
    if (service.isMonitoring) {
      await service.stopMonitor();
      setMonitorActive(false);
    }

    // Reuse a port already granted this session, so the flash -> monitor ->
    // flash-again loop does not re-prompt on every iteration. Disconnect clears
    // it, which is how the user picks a different board.
    if (!service.hasPort) {
      try {
        await service.selectPort();
        setHasPort(true);
      } catch (error) {
        setDiagnosis(diagnose(error, { phase: "select-port" }));
        return;
      }
    }

    setConnection("connecting");
    phaseRef.current = "connect";
    appendLog("info", "Opening the port and looking for a chip in download mode...");

    try {
      const info = await service.connect(settings.baudRate);
      setDevice(info);
      setConnection("connected");
      appendLog("success", `Connected to ${info.description}.`);
      if (info.flashSize) appendLog("info", `Flash size detected: ${info.flashSize}.`);
      if (info.chip?.notes) appendLog("info", info.chip.notes);
    } catch (error) {
      setConnection("disconnected");
      setDiagnosis(
        diagnose(error, {
          phase: "connect",
          baudRate: settings.baudRate,
          usingNativeUsb: device?.usingNativeUsb,
        }),
      );
      // Leave nothing half-open, or the next attempt hits InvalidStateError.
      await service.disconnect().catch(() => {});
    }
  }, [appendLog, device?.usingNativeUsb, service, settings.baudRate]);

  const disconnect = useCallback(async () => {
    await service.disconnect();
    setConnection("disconnected");
    setDevice(null);
    setMonitorActive(false);
    setHasPort(false);
    setProgress(null);
    appendLog("info", "Disconnected.");
  }, [appendLog, service]);

  const startMonitor = useCallback(async () => {
    try {
      phaseRef.current = "monitor";
      await service.startMonitor(115200);
      setMonitorActive(true);
      // The loader session is torn down to free the stream for the console.
      setConnection("disconnected");
      setDevice(null);
      appendLog("info", "Serial monitor open at 115200 baud.");
    } catch (error) {
      setDiagnosis(diagnose(error, { phase: "monitor" }));
    }
  }, [appendLog, service]);

  const stopMonitor = useCallback(async () => {
    await service.stopMonitor();
    setMonitorActive(false);
    appendLog("info", "Serial monitor closed.");
  }, [appendLog, service]);

  const clearMonitor = useCallback(() => setMonitorText(""), []);
  const clearLogs = useCallback(() => setLogs([]), []);

  const flash = useCallback(async () => {
    const selected = parts.filter((part) => part.include);
    if (selected.length === 0 || report.hasErrors) return;

    setDiagnosis(null);
    setFlashState("flashing");
    setProgress(null);

    const regions = selected
      .map((part) => ({ fileName: part.fileName, data: part.data, address: part.offset }))
      // esptool writes in the given order; ascending addresses keep the log readable.
      .sort((a, b) => a.address - b.address);

    appendLog(
      "info",
      `Writing ${regions.length} region${regions.length === 1 ? "" : "s"} at ${settings.baudRate} baud.`,
    );

    try {
      await service.flash(regions, settings);
      setFlashState("success");
      appendLog("success", "Flash complete and verified.");

      if (openMonitorAfterFlash) {
        // The board needs a moment to come out of reset before the port reopens.
        await new Promise((resolve) => setTimeout(resolve, 500));
        await startMonitor();
      }
    } catch (error) {
      setFlashState("failed");
      setDiagnosis(
        diagnose(error, {
          phase: phaseRef.current,
          chipName: device?.chipName,
          baudRate: settings.baudRate,
          usingNativeUsb: device?.usingNativeUsb,
        }),
      );
    }
  }, [
    appendLog,
    device?.chipName,
    device?.usingNativeUsb,
    openMonitorAfterFlash,
    parts,
    report.hasErrors,
    service,
    settings,
    startMonitor,
  ]);

  const resetBoard = useCallback(async () => {
    try {
      await service.resetBoard();
      appendLog("info", "Reset pulse sent.");
    } catch (error) {
      setDiagnosis(diagnose(error, { phase: "reset" }));
    }
  }, [appendLog, service]);

  const canFlash =
    connection === "connected" &&
    flashState !== "flashing" &&
    parts.some((part) => part.include) &&
    !report.hasErrors;

  return {
    environment,
    connection,
    device,
    parts,
    settings,
    flashState,
    progress,
    diagnosis,
    logs,
    monitorText,
    monitorActive,
    openMonitorAfterFlash,
    isReadingFiles,
    assumedChipKey,
    targetChip,
    report,
    canFlash,
    hasPort,

    setSettings: (patch: Partial<FlashSettings>) => setSettings((current) => ({ ...current, ...patch })),
    setAssumedChipKey,
    setOpenMonitorAfterFlash,
    addFiles,
    addFromDataTransfer,
    removePart,
    clearParts,
    setPartOffset,
    resetPartOffset,
    togglePart,
    connect,
    disconnect,
    flash,
    resetBoard,
    startMonitor,
    stopMonitor,
    clearMonitor,
    clearLogs,
    dismissDiagnosis: () => setDiagnosis(null),
  };
}
